import { useMemo } from 'react';
import { useIntl } from 'umi';

/**
 * `validateMessages` for every form in the Email Center.
 *
 * ### Why the default was wrong
 *
 * antd builds its "please enter ${label}" from the `Form.Item`'s label — but
 * **only when the label is a string**. Every label here is a `<F>` element (that
 * is how they are translated), so antd fell back to the field's `name` and showed
 * German copy wrapped around an English column name: *"Bitte geben Sie subject
 * an"*. Thirty required rules across nine drawers had it.
 *
 * The replacement does not interpolate the label at all. It says less than the
 * default would, but the field it sits under is the one it refers to, and "this
 * field is required" is right in every language rather than half-right in one.
 *
 * ### Why a hook rather than a plain call
 *
 * antd treats `validateMessages` as a new value on every render if it is a new
 * object, and one of these sits on the form wrapping the visual editor — the one
 * surface in here whose render cost is already a complaint. Memoised on `intl`,
 * which only changes when the locale does.
 */
export function useEmailCenterValidateMessages(): { required: string } {
  const intl = useIntl();

  return useMemo(
    () => ({
      required: intl.formatMessage({
        id: 'pages.emailCenter.form.required',
        defaultMessage: 'This field is required',
      }),
    }),
    [intl],
  );
}
